import { useCreatePaymentMethod } from '@/api/customers/payment-methods/createPaymentMethod';
import {
  CreateSetupIntentResponse,
  useCreateSetupIntent,
} from '@/api/customers/payment-methods/createSetupIntent';
import { Button } from '@/components/ui/elements/button';
import { STRIPE_KEY } from '@/config';
import { useTheme } from '@/providers/theme';
import { Customer, ModelID } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormInput } from '../Form';
import { FormStateSelect } from '../Form/FormStateSelect';
import { Form } from '../ui/form/form';

interface NewPaymentMethodProps {
  customer: Customer;
  franchiseId: ModelID;
  defaultOpen: boolean;
}

const copyableFields = [
  'address',
  'address_line2',
  'city',
  'state',
  'zipcode',
  'phone',
  'email',
] as const;

const schema = z.object({
  name: z.string().min(1, 'Required'),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  address: z.string(),
  address_line2: z.string().nullish(),
  city: z.string(),
  state: z.string(),
  zipcode: z.string().min(1, 'Required'),
});

type NewPaymentMethodFormValues = z.infer<typeof schema>;

type NewPaymentMethodFormProps = {
  customer: Customer;
  clientSecret: string;
  franchiseId: ModelID;
  onStored: () => void;
};

const elementsOptions = {
  appearance: {
    labels: 'floating',
    theme: 'stripe',
  },
} as StripeElementsOptions;

const NewPaymentMethodForm = ({
  customer,
  clientSecret,
  franchiseId,
  onStored,
}: NewPaymentMethodFormProps) => {
  const [error, setError] = useState<string>();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const { isDarkMode } = useTheme();

  const { mutate: createStoredPaymentMethod, isPending } = useCreatePaymentMethod({
    onError: (error: any) => {
      setError(error?.response?.data?.message);
      setIsProcessing(false);
    },
    onSuccess: () => {
      setIsProcessing(false);
      form.reset();
      elements?.getElement(CardElement)?.clear();
      onStored();
    },
  });

  const form = useForm<NewPaymentMethodFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      address: '',
      address_line2: '',
      city: '',
      state: '',
      zipcode: '',
    },
  });

  const handleCreate = async (data: NewPaymentMethodFormValues) => {
    if (!stripe || !elements || isPending || isProcessing) {
      return;
    }

    setError(undefined);
    setIsProcessing(true);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Unable to load the card form.');
      setIsProcessing(false);
      return;
    }

    const setupIntentResult = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: data.name,
          email: data.email ?? '',
          phone: data.phone ?? '',
          address: {
            line1: data.address ?? '',
            line2: data.address_line2 ?? '',
            city: data.city ?? '',
            state: data.state ?? '',
            postal_code: data.zipcode ?? '',
          },
        },
      },
    });

    if (setupIntentResult.error || !setupIntentResult.setupIntent) {
      setError(setupIntentResult.error?.message ?? 'Unable to save card.');
      setIsProcessing(false);
      return;
    }

    if (setupIntentResult.setupIntent.status !== 'succeeded') {
      setError('Unable to save card.');
      setIsProcessing(false);
      return;
    }

    createStoredPaymentMethod({
      data: {
        ...data,
        customer_id: customer.id,
        franchise_id: franchiseId,
        setup_intent_id: setupIntentResult.setupIntent.id,
      },
    });
  };

  const copyCustomer = () => {
    form.setValue('name', customer.full_name ?? '');

    const cust = customer as unknown as { [k: string]: string };
    copyableFields.map((key: (typeof copyableFields)[number]) =>
      form.setValue(key, cust[key] ?? ''),
    );
  };

  const copyBilling = () => {
    form.setValue('name', customer.billing_name ?? '');

    const cust = customer as unknown as { [k: string]: string };
    copyableFields.map((key: (typeof copyableFields)[number]) =>
      form.setValue(key, cust[`billing_${key}`] ?? ''),
    );
  };

  if (!stripe || !elements) {
    // Stripe.js has not yet loaded.
    return <>Loading...</>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleCreate)}>
        <div className="flex flex-col space-y-3">
          <div>
            <div
              className={clsx(
                'bg-background text-foreground border shadow-xs py-3 px-2 rounded-sm',
                error ? 'border-red-500' : '',
              )}
            >
              <CardElement
                options={{
                  disableLink: true,
                  style: {
                    base: isDarkMode
                      ? {
                          color: 'oklch(0.985 0 0)',
                          backgroundColor: 'oklch(0.141 0.005 285.823)',
                        }
                      : {
                          color: 'oklch(0.141 0.005 285.823)',
                          backgroundColor: 'oklch(1 0 0)',
                        },
                  },
                }}
              />
            </div>
            {!!error && <div className="text-xs pt-1 pl-1 font-semibold text-red-500">{error}</div>}
          </div>

          <FormInput control={form.control} name="name" label="Name on Card" />
          <FormInput control={form.control} name="address" label="Address" />
          <FormInput control={form.control} name="address_line2" label="Address Line 2" />
          <div className="flex flex-row space-x-2">
            <div className="w-3/6">
              <FormInput control={form.control} name="city" label="City" />
            </div>
            <div className="w-2/6">
              <FormStateSelect control={form.control} name="state" label="State" style="abbr" />
            </div>
            <div className="w-2/6">
              <FormInput control={form.control} name="zipcode" label="Zip" />
            </div>
          </div>
          <FormInput control={form.control} name="phone" label="Phone" />
          <FormInput control={form.control} name="email" label="Email" />

          <div className="flex flex-row space-x-2">
            <Button type="button" variant="outline" size="sm" onClick={() => copyCustomer()}>
              Copy Customer
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => copyBilling()}>
              Copy Billing
            </Button>
            <span className="grow" />
            <Button type="submit" disabled={isPending || isProcessing}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export const NewPaymentMethod = ({ customer, franchiseId }: NewPaymentMethodProps) => {
  const [setupIntentContext, setSetupIntentContext] = useState<CreateSetupIntentResponse | null>(
    null,
  );
  const [bootstrapError, setBootstrapError] = useState<string>();

  const { mutateAsync: createSetupIntent, isPending: isBootstrapping } = useCreateSetupIntent();

  const stripePromise = useMemo(() => {
    if (!setupIntentContext?.stripe_account_id) {
      return null;
    }

    return loadStripe(STRIPE_KEY, {
      stripeAccount: String(setupIntentContext.stripe_account_id),
    });
  }, [setupIntentContext?.stripe_account_id]);

  const bootstrapSetupIntent = async () => {
    setBootstrapError(undefined);
    setSetupIntentContext(null);

    try {
      const response = await createSetupIntent({
        customer_id: customer.id,
        franchise_id: franchiseId,
      });

      if (!response?.client_secret || !response?.stripe_account_id || !response?.setup_intent_id) {
        throw new Error('Setup intent response was missing required Stripe fields.');
      }

      setSetupIntentContext(response);
    } catch (error: any) {
      setBootstrapError(
        error?.response?.data?.message ?? error?.message ?? 'Unable to prepare a secure card form.',
      );
    }
  };

  useEffect(() => {
    void bootstrapSetupIntent();
  }, [customer.id, franchiseId]);

  if (bootstrapError) {
    return (
      <div className="flex flex-col space-y-3">
        <div className="text-sm font-medium text-red-500">{bootstrapError}</div>
        <div className="flex flex-row">
          <span className="grow" />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void bootstrapSetupIntent()}
            disabled={isBootstrapping}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!setupIntentContext || !stripePromise) {
    return <>Loading...</>;
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <NewPaymentMethodForm
        customer={customer}
        clientSecret={setupIntentContext.client_secret}
        franchiseId={franchiseId}
        onStored={() => {
          void bootstrapSetupIntent();
        }}
      />
    </Elements>
  );
};
